package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * slotGrid 编译期静态展开阶段（RFC §4.4）。
 *
 * 将每个 `kind = "slotGrid"` 的元素替换为：
 * - 一个 `kind = "box"` 包装容器（layout=absolute、保留原 x/y/positionMode、宽高 = cols*cellSize / rows*cellSize）
 * - N 个 `kind = "slot"` 子元素（按 indexOrder 计算 index、坐标 = col*cellSize + cellPadding 等）
 *
 * dynamic=true 时本阶段不展开，原 slotGrid 元素保留，留待 B4 阶段运行时展开。
 */
class SlotGridExpanderPass(private val diag: DiagnosticCollector) {

    /** 入口：展开 screen root 中的所有 slotGrid。 */
    fun expand(screen: ScreenAst): ScreenAst {
        return screen.copy(root = expandElement(screen.root))
    }

    /** 后序递归：先处理 children，再尝试展开当前节点。 */
    private fun expandElement(element: ElementAst): ElementAst {
        val expandedChildren = element.children.map { expandElement(it) }
        val current = element.copy(children = expandedChildren)
        if (current.kind != "slotGrid") return current
        return expandSlotGrid(current)
    }

    /** 实际展开逻辑：校验 + 计算 + 替换。 */
    private fun expandSlotGrid(grid: ElementAst): ElementAst {
        // dynamic=true 时保留原节点，B4 阶段再处理
        if (isDynamic(grid)) return grid

        val source = findPropValue(grid, "source")
        val cols = findPropValue(grid, "cols")
        val rows = findPropValue(grid, "rows")
        val indexOrderExpr = findPropValue(grid, "indexOrder")

        var hasError = false

        // source 必传（StringLit 非空）
        if (source == null || source !is ExprAst.Literal.StringLit || source.value.isEmpty()) {
            diag.error(DslError.E312_SLOTGRID_SOURCE_MISSING, grid.position, grid.id)
            hasError = true
        }

        // cols 必传，必须正整数
        val colsInt = when {
            cols == null -> {
                diag.error(DslError.E313_SLOTGRID_COLS_MISSING, grid.position, grid.id)
                hasError = true
                null
            }
            else -> {
                val v = asPositiveInt(cols)
                if (v == null) {
                    diag.error(DslError.E315_SLOTGRID_COLS_INVALID, grid.position, grid.id)
                    hasError = true
                }
                v
            }
        }

        // rows 必传，必须正整数（dynamic=false 分支才会到这里）
        val rowsInt = when {
            rows == null -> {
                diag.error(DslError.E314_SLOTGRID_ROWS_MISSING, grid.position, grid.id)
                hasError = true
                null
            }
            else -> {
                val v = asPositiveInt(rows)
                if (v == null) {
                    diag.error(DslError.E316_SLOTGRID_ROWS_INVALID, grid.position, grid.id)
                    hasError = true
                }
                v
            }
        }

        // indexOrder 可省略；存在时必须是三种之一
        val indexOrder = when (indexOrderExpr) {
            null -> IndexOrder.ROW_MAJOR
            is ExprAst.Literal.StringLit -> IndexOrder.fromKey(indexOrderExpr.value).also { resolved ->
                if (resolved == null) {
                    diag.error(
                        DslError.E317_SLOTGRID_INDEX_ORDER_UNKNOWN,
                        indexOrderExpr.position,
                        grid.id,
                        indexOrderExpr.value,
                    )
                    hasError = true
                }
            } ?: IndexOrder.ROW_MAJOR
            else -> {
                diag.error(
                    DslError.E317_SLOTGRID_INDEX_ORDER_UNKNOWN,
                    indexOrderExpr.position,
                    grid.id,
                    "<non-literal>",
                )
                hasError = true
                IndexOrder.ROW_MAJOR
            }
        }

        if (hasError || colsInt == null || rowsInt == null) return grid

        // 提取数值类 prop（带默认值）
        val indexStart = (findPropValue(grid, "indexStart") as? ExprAst.Literal.NumberLit)
            ?.value?.toInt() ?: 0
        val cellSize = (findPropValue(grid, "cellSize") as? ExprAst.Literal.NumberLit)
            ?.value?.toDouble() ?: 18.0
        val cellPadding = (findPropValue(grid, "cellPadding") as? ExprAst.Literal.NumberLit)
            ?.value?.toDouble() ?: 1.0
        val slotSize = cellSize - 2.0 * cellPadding

        // 生成 N 个 slot 子元素
        val slots = buildList {
            for (row in 0 until rowsInt) {
                for (col in 0 until colsInt) {
                    add(buildSlot(grid, row, col, colsInt, rowsInt, indexStart, indexOrder, cellSize, cellPadding, slotSize))
                }
            }
        }

        // 包装容器 props：保留 x/y/positionMode（若有），再加 layout=absolute、width、height
        val boxProps = buildBoxProps(grid, colsInt, rowsInt, cellSize)

        return ElementAst(
            kind = "box",
            id = grid.id,
            props = boxProps,
            children = slots,
            modifiers = grid.modifiers,
            position = grid.position,
        )
    }

    /** 构造单个 slot 子元素。 */
    private fun buildSlot(
        grid: ElementAst,
        row: Int,
        col: Int,
        cols: Int,
        rows: Int,
        indexStart: Int,
        order: IndexOrder,
        cellSize: Double,
        cellPadding: Double,
        slotSize: Double,
    ): ElementAst {
        val index = order.compute(row, col, cols, rows, indexStart)
        val x = col * cellSize + cellPadding
        val y = row * cellSize + cellPadding
        val pos = grid.position

        // 透传共享 prop（仅原 slotGrid 显式声明的项）
        val shared = SHARED_PROPS.mapNotNull { name ->
            grid.props.firstOrNull { it.name == name }
        }
        val ownProps = listOf(
            ElementAst.PropAssign("x", ExprAst.Literal.NumberLit(x, pos), pos),
            ElementAst.PropAssign("y", ExprAst.Literal.NumberLit(y, pos), pos),
            ElementAst.PropAssign("index", ExprAst.Literal.NumberLit(index.toLong(), pos), pos),
            ElementAst.PropAssign("slotSize", ExprAst.Literal.NumberLit(slotSize, pos), pos),
        )

        return ElementAst(
            kind = "slot",
            id = "${grid.id}/slot_${index}",
            props = shared + ownProps,
            children = emptyList(),
            position = pos,
        )
    }

    /** 构造 box 包装容器的 props 列表。 */
    private fun buildBoxProps(grid: ElementAst, cols: Int, rows: Int, cellSize: Double): List<ElementAst.PropAssign> {
        val pos = grid.position
        val result = mutableListOf<ElementAst.PropAssign>()
        // 保留 grid 上的几何 / 定位类 prop（x/y/positionMode/z）
        for (pa in grid.props) {
            if (pa.name in BOX_KEEP_PROPS) result.add(pa)
        }
        // 强制 layout=absolute，子 slot 用绝对坐标定位
        result.add(ElementAst.PropAssign("layout", ExprAst.Literal.StringLit("absolute", pos), pos))
        result.add(ElementAst.PropAssign(
            "width", ExprAst.Literal.NumberLit(cols * cellSize, pos), pos,
        ))
        result.add(ElementAst.PropAssign(
            "height", ExprAst.Literal.NumberLit(rows * cellSize, pos), pos,
        ))
        return result
    }

    // ===== helpers =====

    /** 查 slotGrid 上某个 prop 的字面量表达式；不存在返回 null。 */
    private fun findPropValue(grid: ElementAst, name: String): ExprAst? =
        grid.props.firstOrNull { it.name == name }?.value

    /** 把表达式当作正整数字面量提取；失败返回 null。 */
    private fun asPositiveInt(expr: ExprAst): Int? {
        if (expr !is ExprAst.Literal.NumberLit) return null
        val d = expr.value.toDouble()
        if (d <= 0.0 || d != d.toInt().toDouble()) return null
        return d.toInt()
    }

    /** dynamic prop 为 BoolLit(true) 即视为动态模式。 */
    private fun isDynamic(grid: ElementAst): Boolean {
        val v = findPropValue(grid, "dynamic") ?: return false
        return v is ExprAst.Literal.BoolLit && v.value
    }

    /** 子 slot index 计算策略。 */
    private enum class IndexOrder {
        ROW_MAJOR {
            override fun compute(row: Int, col: Int, cols: Int, rows: Int, start: Int): Int =
                start + row * cols + col
        },
        COL_MAJOR {
            override fun compute(row: Int, col: Int, cols: Int, rows: Int, start: Int): Int =
                start + col * rows + row
        },
        REVERSE_ROW_MAJOR {
            override fun compute(row: Int, col: Int, cols: Int, rows: Int, start: Int): Int =
                start + (cols * rows - 1) - (row * cols + col)
        };

        abstract fun compute(row: Int, col: Int, cols: Int, rows: Int, start: Int): Int

        companion object {
            fun fromKey(key: String): IndexOrder? = when (key) {
                "rowMajor" -> ROW_MAJOR
                "colMajor" -> COL_MAJOR
                "reverseRowMajor" -> REVERSE_ROW_MAJOR
                else -> null
            }
        }
    }

    companion object {
    /** 透传到每个子 slot 的共享 prop 名单。 */
    private val SHARED_PROPS = listOf(
        "source",
        "groupId",
        "key",
        "readOnly",
        "allowShiftTransfer",
        "allowDrop",
        "allowPick",
        "slotType",
        "predict",
        "placeholder",
    )

        /** box 包装容器保留的 grid 原 prop 名单（仅几何/定位类）。 */
        private val BOX_KEEP_PROPS = setOf("x", "y", "positionMode")
    }
}
